import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { observer } from 'mobx-react';
import { NavLink } from 'react-router-dom';
import { useStore } from '../../../../../../stores/root';

// assets
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faHome, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import GeneralService from '../../../../../../services/general';
import EmployeeTargetSummaryService, { ITargetSummaryProps } from '../../../../../../services/employees/targetSummary';

// props
import { IEmployeeContractResourceProps } from '../../../../../../props/employees/contracts/contract';

// components
import { ActionButton, CommandBar, IButtonProps, ICommandBarItemProps, PrimaryButton, ProgressIndicator, Stack } from '@fluentui/react';
import Text from '../../../../../typography/text';
import LoadingComponent from '../../../../../feedbacks/loading';
import Label from '../../../../../typography/label';
import Details from '../../../../../typography/details';
import Tag from '../../../../../uiframeworks/tag';

interface EmployeeContractDetailsPropertiesProps {
    contract: IEmployeeContractResourceProps;
}

const EmployeeContractDetailsProperties: React.FC<EmployeeContractDetailsPropertiesProps> = observer((props: EmployeeContractDetailsPropertiesProps) => {
    const { banner, topbar, user } = useStore();
    const { contract } = props;
    const [targetSummary, setTargetSummary] = useState<ITargetSummaryProps | undefined>();
    const [loadingTarget, setLoadingTarget] = useState<boolean>(false);
    const [selectedGrouping, setSelectedGrouping] = useState<string>('created date');
    const [selectedDate, setSelectedDate] = useState<{ text: string; date: string; period: 'month' | 'year' }>({
        text: 'this month',
        date: moment().startOf('month').toISOString(),
        period: 'month'
    });

    useEffect(() => {
        if (contract?.employee?.id && Number(contract.targetAmount || 0) > 0) {
            setLoadingTarget(true);
            EmployeeTargetSummaryService.get(
                contract.employee.id,
                selectedDate.date,
                selectedGrouping,
                selectedDate.period,
                contract.id
            )
                .then((res) => {
                    setTargetSummary(res);
                    setLoadingTarget(false);
                })
                .catch(() => {
                    setLoadingTarget(false);
                });
        }
    }, [contract?.employee?.id, contract?.id, contract?.targetAmount, selectedDate, selectedGrouping]);

    const percentage = Number(targetSummary?.percentage ?? 0);
    const achievedAmount = Number(targetSummary?.achievedAmount ?? 0);
    const targetAmount = Number(targetSummary?.targetAmount ?? contract.targetAmount ?? 0);
    const remainingAmount = Number(targetSummary?.remainingAmount ?? 0);
    const ordersCount = Number(targetSummary?.ordersCount ?? 0);
    const isTargetMet = Boolean(targetSummary?.isTargetMet);

    const getEmployeeTypeLabel = (type?: string) => {
        switch (type) {
            case 'Permanent': return 'Permanent (PKWTT)';
            case 'Contract': return 'Contract (PKWT)';
            case 'Probation': return 'Probation';
            case 'Freelance': return 'Freelance (No End Date)';
            default: return type || (contract.endDate ? 'Contract (PKWT)' : 'Freelance (No End Date)');
        }
    };

    return <Stack tokens={{ childrenGap: 20 }}>
        <Stack tokens={{ childrenGap: 20 }} horizontal>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Contract number'} text={'#' + contract.id} />
            </Stack>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Employee Type'} text={getEmployeeTypeLabel(contract.employeeType)} />
            </Stack>
        </Stack>
        <Stack tokens={{ childrenGap: 20 }} horizontal>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Employee'} text={contract.employee.name} />
            </Stack>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Subsidiary'} text={contract.subsidiary.name} />
            </Stack>
        </Stack>
        <Stack tokens={{ childrenGap: 20 }} horizontal>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Start date'} text={contract.startDate ? moment(contract.startDate).format("DD/MM/YYYY") : '-'} />
            </Stack>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details
                    title={'End date'}
                    text={contract.endDate ? moment(contract.endDate).format("DD/MM/YYYY") : 'Freelance (No End Date)'}
                />
            </Stack>
        </Stack>
        <Stack className={'divider'}></Stack>
        <Stack tokens={{ childrenGap: 20 }} horizontal>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Base salary'} text={`Rp. ${GeneralService.getNumberWithSeparator(Number(contract.salary))}`} />
            </Stack>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={`Monthly target (${contract.targetType === 'profit' ? 'Profit' : 'Omset'})`} text={`Rp. ${GeneralService.getNumberWithSeparator(Number(contract.targetAmount || 0))}`} />
            </Stack>
        </Stack>

        {Number(contract.targetAmount || 0) > 0 ? (
            <Stack tokens={{ childrenGap: 12 }} style={{
                padding: 16,
                borderRadius: 8,
                backgroundColor: '#faf9f8',
                border: '1px solid #edebe9'
            }}>
                {/* Filter Header */}
                <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'} wrap>
                    <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign={'center'}>
                        <Text size={'xsmall'}>Filter: </Text>
                        <Stack horizontal verticalAlign={'center'}>
                            <Label size={'xsmall'} className={!loadingTarget ? 'color-blue' : 'color-gray'}>{selectedGrouping}</Label>
                            <ActionButton disabled={loadingTarget} menuProps={{
                                items: [
                                    { key: 'created date', text: 'created date', onClick: () => setSelectedGrouping('created date') },
                                    { key: 'loading date', text: 'loading date', onClick: () => setSelectedGrouping('loading date') }
                                ]
                            }} styles={{
                                root: { height: "initial !important", minHeight: "initial !important", padding: "0px !important", top: "-1px !important" }
                            }} />
                        </Stack>
                        <Text size={'xsmall'}> on </Text>
                        <Stack horizontal verticalAlign={'center'}>
                            <Label size={'xsmall'} className={!loadingTarget ? 'color-blue' : 'color-gray'}>{selectedDate.text}</Label>
                            <ActionButton disabled={loadingTarget} menuProps={{
                                items: [
                                    {
                                        key: 'this month', text: 'this month', onClick: () => {
                                            setSelectedDate({
                                                text: 'this month',
                                                date: moment().startOf('month').toISOString(),
                                                period: 'month'
                                            });
                                        }
                                    },
                                    {
                                        key: 'last month', text: 'last month', onClick: () => {
                                            setSelectedDate({
                                                text: 'last month',
                                                date: moment().add(-1, 'month').startOf('month').toISOString(),
                                                period: 'month'
                                            });
                                        }
                                    },
                                    {
                                        key: 'this year', text: 'this year', onClick: () => {
                                            setSelectedDate({
                                                text: 'this year',
                                                date: moment().startOf('year').toISOString(),
                                                period: 'year'
                                            });
                                        }
                                    },
                                    {
                                        key: 'last year', text: 'last year', onClick: () => {
                                            setSelectedDate({
                                                text: 'last year',
                                                date: moment().add(-1, 'year').startOf('year').toISOString(),
                                                period: 'year'
                                            });
                                        }
                                    }
                                ]
                            }} styles={{
                                root: { height: "initial !important", minHeight: "initial !important", padding: "0px !important", top: "-1px !important" }
                            }} />
                        </Stack>
                    </Stack>
                    <Tag size={'small'} text={`Basis: ${contract.targetType === 'profit' ? 'Profit' : 'Omset'}`} />
                </Stack>

                <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'}>
                    <Label size={'small'}>Target Achievement ({selectedDate.period === 'year' ? 'Yearly' : selectedDate.text})</Label>
                    <Label size={'medium'} style={{ color: percentage >= 100 ? '#107c41' : '#0078d4', fontWeight: 600 }}>
                        {`${percentage}% Achieved`}
                    </Label>
                </Stack>

                <ProgressIndicator
                    percentComplete={Math.min(1, percentage / 100)}
                    barHeight={8}
                />

                <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'}>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Text size={'xsmall'}>Realization ({contract.targetType === 'profit' ? 'Nett Profit' : 'Omset'})</Text>
                        <Label size={'small'}>Rp. {GeneralService.getNumberWithSeparator(achievedAmount)}</Label>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }} horizontalAlign={'end'}>
                        <Text size={'xsmall'}>Contract Target {selectedDate.period === 'year' ? '(Yearly)' : '(Monthly)'}</Text>
                        <Label size={'small'}>Rp. {GeneralService.getNumberWithSeparator(targetAmount)}</Label>
                    </Stack>
                </Stack>

                {targetSummary ? (
                    <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'} style={{ marginTop: 4 }}>
                        <Text size={'xsmall'}>Total Orders: {ordersCount}</Text>
                        {isTargetMet ? (
                            <Label size={'xsmall'} style={{ color: '#107c41', fontWeight: 600 }}>Target Met! 🎉</Label>
                        ) : (
                            <Text size={'xsmall'} style={{ color: '#605e5c' }}>
                                Remaining Target: <strong style={{ color: '#323130' }}>Rp. {GeneralService.getNumberWithSeparator(remainingAmount)}</strong>
                            </Text>
                        )}
                    </Stack>
                ) : null}
            </Stack>
        ) : null}

        <Stack className={'divider'}></Stack>
        <Stack tokens={{ childrenGap: 20 }} horizontal>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Annual Leave Quota'} text={`${contract.annualLeaveQuota || 12} Days / Year`} />
            </Stack>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'WFH Quota'} text={`${contract.wfhQuota || 3} Days / Year`} />
            </Stack>
        </Stack>
        <Stack className={'divider'}></Stack>
        <Details title={'Jobdesks'}>
            <Stack tokens={{ childrenGap: 5 }} horizontal wrap styles={{ root: { position: 'relative', top: 3 } }}>
                {(contract.jobdesks || []).map((jobdesk, index) => <Tag key={`jobdesk_${index}`} size={'small'} text={jobdesk.name} />)}
            </Stack>
        </Details>
    </Stack>;
});

export default EmployeeContractDetailsProperties;
